/**
 * Vimeo URL handling.
 *
 * Unlisted Vimeo videos carry a privacy hash, and the player refuses to load
 * without it ("Sorry, we're having a little trouble"). It shows up in two
 * shapes depending on where the creator copied the link from:
 *
 *   https://vimeo.com/1215584049/083f8454d5
 *   https://player.vimeo.com/video/1215584049?h=083f8454d5&badge=0&...
 *
 * So the embed key we store keeps the hash attached (`id?h=hash`) and every
 * player builds `https://player.vimeo.com/video/{key}` from it. Thumbnails are
 * the exception — vumbnail.com wants the bare id — which is what
 * vimeoThumbnailUrl is for.
 *
 * The other query params (badge, autopause, player_id, app_id) are cosmetic and
 * deliberately dropped; only the hash affects whether the video plays.
 */

/** Pull the video id and privacy hash out of any Vimeo URL shape. */
export const parseVimeoUrl = (url) => {
   if (!url) return { id: '', hash: '' };
   const idMatch = String(url).match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/);
   if (!idMatch || !idMatch[1]) return { id: '', hash: '' };
   // Path form (vimeo.com/ID/HASH) first, then query form (?h=HASH).
   const queryHash = String(url).match(/[?&]h=([a-zA-Z0-9]+)/);
   return { id: idMatch[1], hash: idMatch[2] || (queryHash ? queryHash[1] : '') };
};

/**
 * The value to store and to append to https://player.vimeo.com/video/.
 * Returns '' when the URL is not a Vimeo link, so callers can keep using
 * falsy checks the way they did with the old regex.
 */
export const vimeoEmbedKey = (url) => {
   const { id, hash } = parseVimeoUrl(url);
   if (!id) return '';
   return hash ? `${id}?h=${hash}` : id;
};

/** Bare id from a stored key, which may or may not carry ?h=. */
export const vimeoIdFromKey = (key) => String(key || '').split('?')[0];

/** vumbnail only accepts the bare id — the hash would end up inside the filename. */
export const vimeoThumbnailUrl = (key) => `https://vumbnail.com/${vimeoIdFromKey(key)}.jpg`;
